import React, { forwardRef, isValidElement } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import * as Icons from '@polestar/component-warehouse-react/tokens/icons/Icons';
import Icon, { IconSizes } from '@polestar/component-warehouse-react/components/icon/Icon';
import Spacings from '@polestar/component-warehouse-react/tokens/spacings/Spacings';
import { minimalHitbox, overflowEllipsis, overflowTextOffsetHorizontal, } from '@polestar/component-warehouse-react/helpers/style';
import Alignments, { MainAxisAlignments, } from '@polestar/component-warehouse-react/tokens/alignments/Alignments';
import Sizes from '@polestar/component-warehouse-react/tokens/sizes/Sizes';
import { responsiveStyles } from '@polestar/component-warehouse-react/helpers/responsiveStyle';
import FontSizes from '@polestar/component-warehouse-react/tokens/font-sizes/FontSizes';
import LetterSpacings from '@polestar/component-warehouse-react/tokens/letter-spacings/LetterSpacings';
import LineHeights from '@polestar/component-warehouse-react/tokens/line-heights/LineHeights';
import Spinner, { SpinnerTypes, } from '@polestar/component-warehouse-react/components/spinner/Spinner';
import { useInternalApp } from '@polestar/component-warehouse-react/contexts/internal';
import { useButton } from '@polestar/component-warehouse-react/components/button/Button.helpers';
import { focusProviderOptions } from '@polestar/component-warehouse-react/contexts/focus';
import { useThemeType } from '@polestar/component-warehouse-react/hooks/useTheme';
import { ButtonMotionContextProvider } from '@polestar/component-warehouse-react/components/button/internal/buttonMotion';
import { createCSSTransition, MotionDurations, MotionEasings, } from '@polestar/component-warehouse-react/tokens/motion/Motion';
import { createCustomSpacing } from '@polestar/component-warehouse-react/tokens/spacings/Spacings';
export var ButtonVariants;
(function (ButtonVariants) {
    ButtonVariants["primary"] = "primary";
    ButtonVariants["secondary"] = "secondary";
    ButtonVariants["destructive"] = "destructive";
    ButtonVariants["text"] = "text";
    ButtonVariants["internal"] = "internal";
})(ButtonVariants || (ButtonVariants = {}));
export const AnchorButton = forwardRef(({ children, variant = ButtonVariants.primary, icon = false, size = Sizes.medium, iconPosition = Alignments.right, stretch = false, iconAnimation = true, monochrome = false, ...props }, ref) => {
    const internalApp = useInternalApp();
    const themeType = useThemeType();
    if (variant === ButtonVariants.internal && !internalApp) {
        // eslint-disable-next-line no-console
        console.warn('[AnchorButton] you are trying to use the internal variant that can only be set with internal projects.');
        variant = ButtonVariants.primary;
    }
    if (props.target && props.target === '_blank' && props.rel === undefined) {
        // eslint-disable-next-line no-console
        console.warn('[AnchorButton] Please provide `rel = "noopener" | "noreferrer"`, because of security considerations when using `target="_blank"`. More information at https://web.dev/external-anchors-use-rel-noopener/');
    }
    const { active, hover, ...buttonEvents } = useButton(props);
    return (React.createElement(ButtonMotionContextProvider, { active: active, hover: hover, iconAnimation: iconAnimation },
        React.createElement(AnchorContainer, { ref: ref, ...focusProviderOptions({
                themeType,
            }), ...props, variant: variant, size: size, stretch: stretch, active: active, monochrome: monochrome, ...buttonEvents },
            React.createElement(ButtonContent, { variant: variant, icon: icon, iconPosition: iconPosition, stretch: stretch, size: size, hover: hover, iconAnimation: iconAnimation, monochrome: monochrome }, children))));
});
AnchorButton.displayName = 'AnchorButton';
export const PrimaryAnchorButton = forwardRef((props, ref) => {
    return (React.createElement(AnchorButton, { ref: ref, icon: Icons.arrowRight, ...props, variant: ButtonVariants.primary }));
});
PrimaryAnchorButton.displayName = 'PrimaryAnchorButton';
export const SecondaryAnchorButton = forwardRef((props, ref) => {
    return (React.createElement(AnchorButton, { ref: ref, icon: Icons.arrowRight, ...props, variant: ButtonVariants.secondary }));
});
SecondaryAnchorButton.displayName = 'SecondaryAnchorButton';
export const DestructiveAnchorButton = forwardRef((props, ref) => {
    return (React.createElement(AnchorButton, { ref: ref, icon: Icons.trash, ...props, variant: ButtonVariants.destructive, iconPosition: Alignments.left }));
});
DestructiveAnchorButton.displayName = 'DestructiveAnchorButton';
export const TextAnchorButton = forwardRef((props, ref) => {
    return (React.createElement(AnchorButton, { ref: ref, icon: Icons.arrowRight, ...focusProviderOptions({
            spacing: 4,
        }), ...props, variant: ButtonVariants.text }));
});
TextAnchorButton.displayName = 'TextAnchorButton';
export const Button = forwardRef(({ children, variant = ButtonVariants.primary, size = Sizes.medium, icon = false, 
// TODO: AnchorButton is the default the opposite, should we align?
iconPosition = Alignments.left, loading = false, stretch = false, iconAnimation = true, monochrome = false, ...props }, ref) => {
    const internalApp = useInternalApp();
    const themeType = useThemeType();
    if (variant === ButtonVariants.internal && !internalApp) {
        // eslint-disable-next-line no-console
        console.warn('[Button] you are trying to use the internal variant that can only be set with internal projects.');
        variant = ButtonVariants.primary;
    }
    const customContent = isValidElement(children) && children.type === InnerContent;
    const { active, hover, ...buttonEvents } = useButton(props);
    return (React.createElement(ButtonMotionContextProvider, { active: active, hover: hover, iconAnimation: iconAnimation },
        React.createElement(ButtonContainer, { ref: ref, type: "button", ...focusProviderOptions({
                themeType,
            }), ...props, variant: variant, size: size, stretch: stretch, active: active, monochrome: monochrome, ...buttonEvents }, !customContent ? (React.createElement(ButtonContent, { variant: variant, icon: icon, iconPosition: iconPosition, loading: loading, stretch: stretch, size: size, hover: hover, iconAnimation: iconAnimation, monochrome: monochrome }, children)) : (children))));
});
Button.displayName = 'Button';
export const PrimaryButton = forwardRef(({ ...props }, ref) => React.createElement(Button, { ref: ref, ...props, variant: ButtonVariants.primary }));
PrimaryButton.displayName = 'PrimaryButton';
export const SecondaryButton = forwardRef(({ ...props }, ref) => React.createElement(Button, { ref: ref, ...props, variant: ButtonVariants.secondary }));
SecondaryButton.displayName = 'SecondaryButton';
export const DestructiveButton = forwardRef(({ ...props }, ref) => (React.createElement(Button, { ref: ref, ...props, variant: ButtonVariants.destructive, iconPosition: Alignments.left })));
DestructiveButton.displayName = 'DestructiveButton';
export const TextButton = forwardRef(({ ...props }, ref) => {
    return (React.createElement(Button, { ref: ref, ...focusProviderOptions({
            spacing: 4,
        }), ...props, variant: ButtonVariants.text }));
});
TextButton.displayName = 'TextButton';
const ButtonContent = ({ icon, variant, iconPosition, stretch, size, loading = false, children, }) => {
    const iconElement = !!icon && (React.createElement(StyledIcon, { "data-type": "icon", icon: icon, buttonSize: size, size: size === Sizes.large ? IconSizes.xLarge : IconSizes.medium, variant: variant, position: iconPosition }));
    return (React.createElement(InnerContent, { variant: variant },
        !!loading && iconPosition === Alignments.left ? (React.createElement(StyledSpinner, { as: "span", type: variant === ButtonVariants.text ||
                variant === ButtonVariants.internal ||
                variant === ButtonVariants.secondary
                ? SpinnerTypes.default
                : SpinnerTypes.inverted, alignment: Alignments.left })) : (!!iconElement && iconPosition === Alignments.left && iconElement),
        React.createElement(ButtonLabel, { stretch: stretch }, children),
        !!loading && iconPosition === Alignments.right ? (React.createElement(StyledSpinner, { as: "span", type: variant === ButtonVariants.text ||
                variant === ButtonVariants.internal ||
                variant === ButtonVariants.secondary
                ? SpinnerTypes.default
                : SpinnerTypes.inverted, alignment: Alignments.right })) : (!!iconElement && iconPosition === Alignments.right && iconElement)));
};
const stateCss = (states, state, monochrome) => css(!!states[state].backgroundColor &&
    css `
        background: ${states[state].backgroundColor};
      `, !!states[state].borderColor &&
    css `
        box-shadow: inset 0 0 0 1px ${states[state].borderColor};
      `, css `
      color: ${states[state].color};

      [data-type='icon'] {
        color: ${states[state][monochrome
    ? states[state].monochromeIconColor
        ? `monochromeIconColor`
        : `iconColor`
    : `iconColor`]};
      }
    `);
const sharedButtonStyles = ({ theme, variant, stretch, size, active, monochrome, }) => {
    const states = theme.button[variant].states;
    const sizes = theme.button[ButtonVariants.text].sizes;
    return css(css `
      padding: 0;
      background: none;
      font-weight: normal;
      cursor: pointer;
      position: relative;
      display: inline-block;
      text-decoration: none;
      user-select: none;
      text-overflow: ellipsis;
      touch-action: manipulation;
      -webkit-tap-highlight-color: transparent;
      max-width: 100%;
      font-size: ${FontSizes.body};
      letter-spacing: ${LetterSpacings.small};
      min-height: ${LineHeights.small};

      transition: ${createCSSTransition('all', MotionEasings.ease, MotionDurations.fast)};
      transition-property: background, box-shadow, color;

      ${stateCss(states, 'enabled', monochrome)};

      &:disabled {
        pointer-events: none;
        opacity: 1;
        ${stateCss(states, 'disabled', monochrome)};
      }
      /* Active state for touch devices */
      @media (hover: none) {
        &:active:not(:disabled) {
          ${stateCss(states, 'hover', monochrome)};
        }
      }
    `, !active
        ? css `
          @media (hover: hover) {
            &:hover:not(:disabled) {
              ${stateCss(states, 'hover', monochrome)};
            }
          }
        `
        : css `
          ${stateCss(states, 'active', monochrome)};
        `, stretch &&
        css `
        width: 100%;
      `, variant !== ButtonVariants.text &&
        css `
        height: ${Spacings.xxLarge};
        padding-block: 0;
        padding-inline: ${Spacings.medium} ${stretch ? Spacings.xLarge : Spacings.xLarge};
      `, variant === ButtonVariants.text &&
        size === Sizes.large &&
        sizes &&
        responsiveStyles(sizes.large), minimalHitbox);
};
const AnchorContainer = styled.a((props) => sharedButtonStyles(props));
const ButtonContainer = styled.button((props) => css(css `
      border: none;
    `, sharedButtonStyles(props), css `
      appearance: none;
      overflow: visible;
      display: inline-flex;
      align-items: flex-start;
    `));
const StyledIcon = styled(Icon)(({ position, buttonSize }) => css(css `
      display: flex;
      justify-content: ${MainAxisAlignments.center};
      min-width: ${buttonSize === Sizes.medium ? Spacings.medium : Spacings.xLarge};
      margin-inline-start: ${createCustomSpacing(6 / 16)};
    `, position === Alignments.left &&
    css `
        margin-inline-start: 0;
        margin-inline-end: ${createCustomSpacing(6 / 16)};
      `));
/**
 * @internal
 */
export const InnerContent = styled.span(({ variant }) => css(css `
      flex: 1;
      display: flex;
      align-items: center;
      line-height: 2em;
      height: 1em;
      padding-block-start: ${createCustomSpacing(2 / 16)};
      max-width: 100%;
    `, 
/**
 * It feels weird to center it like this, but because label size is 17px, you can't center is on a 48px high button.
 * This means that the label is aligned on the baseline, not on the center of the text
 */
variant !== ButtonVariants.text
    ? css `
          transform: translateY(${Spacings.medium});
        `
    : css ``));
const ButtonLabel = styled.span(({}) => css(css `
    text-align: start;
    white-space: nowrap;
    ${overflowEllipsis};
    ${overflowTextOffsetHorizontal};
  `));
const StyledSpinner = styled(Spinner)(({ alignment }) => css(css `
        flex: 0;
        max-width: ${Spacings.small};
        margin-block-start: -2px;
      `, alignment === Alignments.left &&
    css `
          margin-inline-end: ${Spacings.xSmall};
        `, alignment === Alignments.right &&
    css `
          margin-inline-start: ${Spacings.xSmall};
        `));
